import { useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch, useStore } from 'react-redux';
import {
  createNewPlayer, checkIn, checkInWithPriority, setPlayerTeam,
  addPriority, removePriority, toggleStayIn, checkOut,
  addScoreToBoard, cancelScoreToBoard, resetScore, setPointsHistory, registerPointRally,
  startGameCounter, stopGameCounter, incrementQueueWaiting,
  resolveGame, resolveGameTeams, addMatchToHistory,
} from 'redux/slices';
import { suggestNextTeam } from '../utils/suggestTeam';
import { canJoinTeam, planFillTeam, teamSize } from '../utils/teamOps';
import { getPlayerPoints } from '../utils/points';

const WORKER_URL = process.env.REACT_APP_WORKER_URL || 'wss://suh-pelada-scoreboard.pandlr.workers.dev';

// Roda no APP (fonte da verdade), em qualquer aba. Publica o estado, aplica comandos
// e aplica o placar remoto no Redux (para marcar mesmo fora da aba Marcador).
export function useRemoteSync() {
  const dispatch = useDispatch();
  // Comandos leem o estado direto do store (sempre atual, mesmo entre renders).
  const store = useStore();
  const players = useSelector((state) => state.players);
  const teams = useSelector((state) => state.teams);
  const game = useSelector((state) => state.game);
  const { remoteControlEnabled } = game;

  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const snapshotRef = useRef(null);

  // Sugestão de próximo time (balanceada) calculada pelo app.
  const nextTeamSuggestion = useMemo(() => suggestNextTeam({
    players,
    playersPerTeam: game.playersPerTeam,
    genderBalanceMode: game.genderBalanceMode,
    genderRestrictedMode: game.genderRestrictedMode,
    femalesPerTeam: game.femalesPerTeam,
    setterRestrictedMode: game.setterRestrictedMode,
    autoBalanceMode: game.autoBalanceMode,
  }), [
    players,
    game.playersPerTeam,
    game.genderBalanceMode,
    game.genderRestrictedMode,
    game.femalesPerTeam,
    game.setterRestrictedMode,
    game.autoBalanceMode,
  ]);

  // Snapshot atual — inclui o placar (o app/tablet é a fonte da verdade).
  const snapshot = {
    players,
    teams,
    nextTeamSuggestion,
    game: {
      playersPerTeam: game.playersPerTeam,
      genderBalanceMode: game.genderBalanceMode,
      genderRestrictedMode: game.genderRestrictedMode,
      femalesPerTeam: game.femalesPerTeam,
      setterRestrictedMode: game.setterRestrictedMode,
      autoBalanceMode: game.autoBalanceMode,
      gameStarted: game.gameStarted,
      scoreA: game.scoreA,
      scoreB: game.scoreB,
    },
  };
  snapshotRef.current = snapshot;

  const publish = () => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'state', data: snapshotRef.current }));
    }
  };

  // Marca/desmarca ponto no Redux (aplicado pelo app, que é a fonte da verdade).
  const addPoint = (team) => {
    if (team !== 'A' && team !== 'B') return;
    const g = store.getState().game;
    if (!g.gameStarted) return;
    dispatch(addScoreToBoard(team));
    dispatch(setPointsHistory([...g.pointsHistory, team]));
    dispatch(registerPointRally());
  };
  const removePoint = (team) => {
    if (team !== 'A' && team !== 'B') return;
    const g = store.getState().game;
    if (!g.gameStarted || (team === 'A' ? g.scoreA : g.scoreB) === 0) return;
    dispatch(cancelScoreToBoard(team));
    dispatch(setPointsHistory(g.pointsHistory.slice(0, -1)));
  };

  // Aplica um comando vindo do remoto.
  const applyCommand = (cmd) => {
    if (!cmd || !cmd.type) return;
    switch (cmd.type) {
      case 'addScore':
        addPoint(cmd.payload);
        break;
      case 'removeScore':
        removePoint(cmd.payload);
        break;
      case 'resetScore':
        dispatch(resetScore());
        dispatch(setPointsHistory([]));
        break;
      case 'createNewPlayer': {
        const list = store.getState().players;
        const playerName = String(cmd.payload?.playerName || '').trim();
        if (!playerName) break;
        const sameName = (p) => p.playerName.trim().toLowerCase() === playerName.toLowerCase();
        if (list.some(sameName)) break;
        const ids = list.map((p) => Number(p.playerId)).filter(Number.isFinite);
        const playerId = ids.length ? Math.max(...ids) + 1 : 1;
        dispatch(createNewPlayer({
          playerId,
          playerName,
          totalPoints: Math.min(100, Math.max(0, Number(cmd.payload.totalPoints) || 0)),
          gender: cmd.payload.gender || 'M',
        }));
        break;
      }
      case 'checkIn':
        dispatch(checkIn(cmd.payload));
        break;
      case 'checkInWithPriority':
        dispatch(checkInWithPriority(cmd.payload));
        break;
      case 'setPlayerTeam': {
        // Valida com o estado atual (o remoto pode estar desatualizado ou ter tocado duas vezes).
        const { players: list, game: g } = store.getState();
        const { playerId, teamId } = cmd.payload || {};
        if (canJoinTeam(list, g.playersPerTeam, playerId, teamId)) {
          dispatch(setPlayerTeam({ playerId, teamId }));
        }
        break;
      }
      case 'fillTeam': {
        // Preenche o time com vaga (auto) com os jogadores escolhidos que ainda estão na fila.
        const { players: list, game: g } = store.getState();
        const plan = planFillTeam({
          players: list,
          playersPerTeam: g.playersPerTeam,
          playerIds: cmd.payload?.playerIds || [],
          priorityIds: cmd.payload?.priorityIds || [],
        });
        if (plan) {
          plan.playerIds.forEach((playerId) => {
            dispatch(setPlayerTeam({ playerId, teamId: plan.targetTeamId }));
          });
          plan.priorityIds.forEach((playerId) => dispatch(addPriority(playerId)));
        }
        break;
      }
      case 'applyAdjustment': {
        const exists = (id) => store.getState().players.some((p) => p.playerId === id);
        (cmd.payload?.setTeamOps || [])
          .filter((op) => exists(op.playerId))
          .forEach((op) => dispatch(setPlayerTeam(op)));
        [...new Set(cmd.payload?.priorityIds || [])]
          .filter(exists)
          .forEach((playerId) => dispatch(addPriority(playerId)));
        break;
      }
      case 'addPriority':
        dispatch(addPriority(cmd.payload));
        break;
      case 'removePriority':
        dispatch(removePriority(cmd.payload));
        break;
      case 'toggleStayIn':
        dispatch(toggleStayIn(cmd.payload));
        break;
      case 'checkOut':
        dispatch(checkOut(cmd.payload));
        break;
      case 'startGame': {
        const { players: list, game: g } = store.getState();
        const aFull = teamSize(list, 1) === g.playersPerTeam;
        const bFull = teamSize(list, 2) === g.playersPerTeam;
        if (aFull && bFull && !g.gameStarted) {
          dispatch(incrementQueueWaiting());
          dispatch(startGameCounter());
        }
        break;
      }
      case 'endGame': {
        const { game: g, players: list, teams: tms } = store.getState();
        // Sem vencedor definido (0x0 ou empate) não encerra.
        if (!g.gameStarted || g.scoreA === g.scoreB) break;
        const WINNER = g.scoreA > g.scoreB ? 1 : 2;
        const LOSER = WINNER === 1 ? 2 : 1;
        const params = {
          winnerTeamId: WINNER,
          loserTeamId: LOSER,
          winnerStays: true,
          loserStays: false,
          saveWinner: false,
        };
        const snapTeam = (teamId) => {
          const tp = list.filter((p) => p.teamId === teamId);
          return {
            name: tms.find((t) => t.id === teamId)?.name || `Time ${teamId === 1 ? 'A' : 'B'}`,
            points: tp.reduce((s, p) => s + getPlayerPoints(p), 0),
            players: tp.map((p) => ({ playerName: p.playerName, gender: p.gender })),
          };
        };
        dispatch(addMatchToHistory({
          playedAt: Date.now(),
          scoreA: g.scoreA,
          scoreB: g.scoreB,
          winner: WINNER === 1 ? 'A' : 'B',
          durationSeconds: (g.time.minutes * 60) + g.time.seconds,
          maxRallySeconds: g.maxRallySeconds,
          teamA: snapTeam(1),
          teamB: snapTeam(2),
        }));
        dispatch(stopGameCounter());
        dispatch(resolveGameTeams(params));
        dispatch(resolveGame(params));
        dispatch(setPointsHistory([WINNER === 1 ? 'A' : 'B']));
        break;
      }
      default:
        break;
    }
  };

  // Conexão + aplicação de comandos/placar.
  useEffect(() => {
    if (!remoteControlEnabled) return undefined;

    let closedByUs = false;

    const connect = () => {
      const ws = new WebSocket(WORKER_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        publish(); // envia o estado atual (inclui placar) assim que conecta
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.type === 'command') applyCommand(message.data);
        } catch (err) {
          // ignora mensagens inválidas
        }
      };

      ws.onclose = () => {
        if (wsRef.current === ws) wsRef.current = null;
        if (!closedByUs) reconnectRef.current = setTimeout(connect, 3000);
      };
    };

    connect();

    return () => {
      closedByUs = true;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) wsRef.current.close();
      wsRef.current = null;
    };
  }, [remoteControlEnabled]);

  // Publica o snapshot sempre que jogadores/times/config mudarem.
  useEffect(() => {
    if (!remoteControlEnabled) return;
    publish();
  }, [
    remoteControlEnabled,
    players,
    teams,
    game.playersPerTeam,
    game.genderBalanceMode,
    game.genderRestrictedMode,
    game.femalesPerTeam,
    game.setterRestrictedMode,
    game.autoBalanceMode,
    game.gameStarted,
    game.scoreA,
    game.scoreB,
  ]);
}
